<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { billingService, type BillingPlan, type WalletRegistry, type TokenSources, type OrgUsageSnapshot } from '@/services/billing';
import SectionTabs, { type SectionTab } from '@/components/shell/SectionTabs.vue';
import MetricIcon from '@/components/shell/MetricIcon.vue';

const activeTab = ref<string>('plan');

const billingTabs = computed<SectionTab[]>(() => [
  { id: 'plan', label: 'Plan' },
  { id: 'tokens', label: 'Tokens' },
  { id: 'expansions', label: 'Expansions' },
]);

// Use BillingPlan end-to-end — we need `limits` and `updatedAt` for dedupe logic
// when live Stripe contains both legacy (pre-bootstrap) and current prices.
type Plan = BillingPlan;

// Canonical subscription plan names — anything else recurring is NOT a subscription
// (it's an expansion) and must not render in the main plans grid.
const CANONICAL_PLAN_NAMES = ['Free Plan', 'Professional Plan', 'Business Plan'];

// Features that unlock IR-internal expansions (OpenSearch / DuckDB).
// Expansions whose display name contains "(IR Internal)" are hidden unless
// the org has one of these features on its JWT claims.
const IR_INTERNAL_FEATURES = ['analytics.opensearch_addon', 'analytics.duckdb'];

const plans = ref<Plan[]>([]);
const isLoading = ref(true);
const plansError = ref('');
const orgError = ref('');

const wallets = ref<WalletRegistry>({});
const limits = ref<Record<string, number>>({});
const orgFeatures = ref<string[]>([]);
const usage = ref<OrgUsageSnapshot | null>(null);

onMounted(async () => {
  try {
    const [plansResult, orgResult, usageResult] = await Promise.allSettled([
      billingService.getPlans(),
      billingService.getOrgDetails(),
      billingService.getOrgUsage(),
    ]);

    if (plansResult.status === 'fulfilled') {
      plans.value = plansResult.value;
    } else {
      console.error("Failed to load plans", plansResult.reason);
      plansError.value = "Could not load subscription plans.";
    }

    if (orgResult.status === 'fulfilled') {
      wallets.value = orgResult.value.wallets;
      limits.value = orgResult.value.limits;
      orgFeatures.value = orgResult.value.features;
    } else {
      console.error("Failed to load organisation billing details", orgResult.reason);
      orgError.value = "Could not load your billing details.";
    }

    if (usageResult.status === 'fulfilled') {
      usage.value = usageResult.value;
    } else {
      // Non-fatal — limits still show without usage overlay.
      console.warn("Failed to load org usage snapshot", usageResult.reason);
    }
  } catch (e) {
    console.error("Error loading billing data", e);
    plansError.value = plansError.value || "Failed to load billing information.";
  } finally {
    isLoading.value = false;
  }
});

// Resolve a "used / limit" display for a given limit key. Returns null when
// we can't pair a usage value to the limit — caller falls back to showing
// just the limit number.
const getUsageForLimit = (key: string): { used: number | null; hint?: string } | null => {
  if (!usage.value) return null;
  switch (key) {
    case 'campaigns':
      return { used: usage.value.campaigns.active, hint: 'active' };
    case 'tenants':
      return { used: usage.value.tenants.used };
    case 'org_users':
      return { used: usage.value.teamMembers.used };
    case 'registrations_included':
    case 'registrations_hard_cap':
      return { used: usage.value.registrations.used };
    default:
      return null;
  }
};

const formatUsageCell = (limitKey: string, limitValue: number): string => {
  const u = getUsageForLimit(limitKey);
  if (!u || u.used === null || u.used === undefined) {
    return String(limitValue);
  }
  return `${u.used} / ${limitValue}`;
};

// Explicit display labels for wallet-bucket and limit keys. Anything not in
// this map falls through to a generic snake_case → Title Case formatter.
// session_tokens and gameplay_tokens are the SAME concept (product term:
// "Gameplay Sessions", UI term: "Gameplays"). Prod stripe grants land in
// session_tokens today; the gameplay_tokens name is the eventual canonical
// but unused in prod. Both labelled "Gameplays" so the UI reads the same
// regardless of which bucket carries the balance.
const NAME_OVERRIDES: Record<string, string> = {
  session_tokens: 'Gameplays',
  gameplay_tokens: 'Gameplays',
  user_session_tokens: 'User Sessions',
  prize_allocation_tokens: 'Prize Allocations',
  registration_tokens: 'Registrations',
  generation_tokens: 'Generation Tokens',
  whatsapp_credits: 'WhatsApp Messages',
  // Other legacy names.
  voucher_tokens: 'Prize Allocations (legacy)',
  login_tokens: 'Registrations (legacy)',
  registrations_included: 'Registrations / mo',
  registrations_hard_cap: 'Registration hard cap',
  org_users: 'Team members',
  tenants: 'Sub-brand tenants',
};

// Wallet buckets rendered on the Token Balance panel. Kept deliberately
// minimal: only the four buckets that represent real product value today.
// session_tokens + gameplay_tokens are the same concept ("Gameplays") and
// get MERGED into a single tile by displayWallets below -- never rendered
// side-by-side. user_session_tokens and prize_allocation_tokens are defined
// in the data model but don't burn anywhere in the hot path yet, so we hide
// them to avoid showing dead zeros.
const VISIBLE_WALLET_KEYS = new Set([
  'session_tokens',
  'registration_tokens',
  'generation_tokens',
  'whatsapp_credits',
]);

// Merge session_tokens + gameplay_tokens balances into a single synthetic
// "session_tokens" key (which renders as "Gameplays" via NAME_OVERRIDES).
// During the legacy->canonical rename window both names may carry balance;
// showing one tile is the source of truth for what the user has.
const displayWallets = computed(() => {
  const raw = wallets.value || {};
  const out: WalletRegistry = {};

  const gameplayBuckets: TokenSources[] = [];
  if (raw.session_tokens) gameplayBuckets.push(raw.session_tokens);
  if (raw.gameplay_tokens) gameplayBuckets.push(raw.gameplay_tokens);

  if (gameplayBuckets.length > 0) {
    const merged: TokenSources = { subscription: 0, purchased: 0 };
    for (const src of gameplayBuckets) {
      for (const [k, v] of Object.entries(src)) {
        merged[k] = (merged[k] || 0) + (v || 0);
      }
    }
    out.session_tokens = merged;
  }

  for (const key of VISIBLE_WALLET_KEYS) {
    if (key === 'session_tokens') continue;
    if (raw[key]) out[key] = raw[key];
  }

  return out;
});

const formatName = (key: string) =>
  NAME_OVERRIDES[key] ??
  key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const getTokenTotal = (sources: TokenSources) =>
  Object.values(sources).reduce((sum, val) => sum + (val || 0), 0);

// Subscriptions: only the three canonical plan names, deduped if live Stripe
// still carries orphan legacy prices (pre-bootstrap). Dedupe rule: prefer the
// plan with populated limits (our new SKUs have limits.campaigns etc.; orphans
// have empty limits), then the most recently updated.
const subscriptions = computed(() => {
  const candidates = plans.value.filter(
    p => CANONICAL_PLAN_NAMES.includes(p.name) && p.interval === 'month' && p.amount >= 0
  );

  candidates.sort((a, b) => {
    const aHas = a.limits && Object.keys(a.limits).length > 0 ? 1 : 0;
    const bHas = b.limits && Object.keys(b.limits).length > 0 ? 1 : 0;
    if (aHas !== bHas) return bHas - aHas;
    const aUpdated = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bUpdated = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return bUpdated - aUpdated;
  });

  const seen = new Set<string>();
  const deduped: Plan[] = [];
  for (const p of candidates) {
    if (seen.has(p.name)) continue;
    seen.add(p.name);
    deduped.push(p);
  }

  return deduped.sort((a, b) => a.amount - b.amount);
});

// Top-ups: one-off consumable grants. Identified by display-name prefix
// "Top-up:" which the catalog uses consistently.
const tokenTopUps = computed(() =>
  plans.value
    .filter(p => p.name.startsWith('Top-up:'))
    .sort((a, b) => a.amount - b.amount)
);

// Expansions: recurring capacity add-ons. Identified by "Expansion:" prefix.
// IR-internal expansions are hidden unless the org has one of the IR features
// on its JWT (OpenSearch / DuckDB entitlements).
const expansions = computed(() => {
  const hasIrAccess = IR_INTERNAL_FEATURES.some(f => orgFeatures.value.includes(f));
  return plans.value
    .filter(p => p.name.startsWith('Expansion:'))
    .filter(p => hasIrAccess || !p.name.includes('(IR Internal)'))
    .sort((a, b) => a.amount - b.amount);
});

function formatPrice(amount: number, currency: string) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(amount / 100);
}

function formatFeature(feature: string) {
  return feature.replace(/\./g, ' ').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function isProfessional(plan: Plan) {
  return plan.name.toLowerCase().includes('professional');
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n);
}

// Human-readable label + singular/plural form for each plan limit key.
const LIMIT_LABELS: Record<string, { singular: string; plural: string }> = {
  campaigns: { singular: 'campaign', plural: 'campaigns' },
  org_users: { singular: 'team member', plural: 'team members' },
  tenants: { singular: 'sub-brand tenant', plural: 'sub-brand tenants' },
  // Registrations get bespoke rendering (included + cap) in buildLimitSummary.
};

// Human-readable label for token grant types shown on plan cards.
const TOKEN_LABELS: Record<string, string> = {
  session_tokens: 'gameplays',
  gameplay_tokens: 'gameplays',
  user_session_tokens: 'user sessions',
  prize_allocation_tokens: 'prize allocations',
  registration_tokens: 'registrations',
  generation_tokens: 'generation tokens',
  whatsapp_credits: 'WhatsApp messages',
  // Legacy aliases so pre-rename plans on live Stripe still resolve sensibly.
  // session_tokens is handled at the top of this map (same label as gameplay_tokens).
  voucher_tokens: 'prize allocations',
  login_tokens: 'registrations',
};

type HighlightItem = { key: string; label: string; detail?: string };
type HighlightGroup = { title: string; items: HighlightItem[] };

/**
 * Build the grouped "what you get" structure shown on a plan card.
 *
 * Three sections in this order:
 *   1. Capacity       — campaigns, team members, sub-brand tenants (hard limits)
 *   2. Monthly        — registration points, gameplay sessions, voucher claims
 *   3. AI & messaging — generation tokens, WhatsApp messages (shown only if > 0,
 *                       so the Free tier doesn't advertise things it doesn't issue)
 *
 * Empty groups are pruned. The "up to N with top-ups" detail on registrations
 * renders as a secondary sub-line under the main bullet.
 */
function buildPlanHighlights(plan: Plan): HighlightGroup[] {
  const limits = plan.limits || {};

  // Aggregate subscription-bucket grants from the plan's tokenGrants array.
  // "purchased" bucket grants are one-off top-ups and don't belong on a tier card.
  const grantMap = new Map<string, number>();
  for (const g of plan.tokenGrants || []) {
    if (g.bucket === 'subscription') {
      grantMap.set(g.token_type, (grantMap.get(g.token_type) || 0) + g.amount);
    }
  }

  const capacity: HighlightItem[] = [];
  if (typeof limits.campaigns === 'number') {
    const l = LIMIT_LABELS.campaigns;
    capacity.push({
      key: 'campaigns',
      label: `${formatNumber(limits.campaigns)} ${limits.campaigns === 1 ? l.singular : l.plural}`,
    });
  }
  if (typeof limits.org_users === 'number') {
    const l = LIMIT_LABELS.org_users;
    capacity.push({
      key: 'org_users',
      label: `${formatNumber(limits.org_users)} ${limits.org_users === 1 ? l.singular : l.plural}`,
    });
  }
  if (typeof limits.tenants === 'number') {
    const l = LIMIT_LABELS.tenants;
    capacity.push({
      key: 'tenants',
      label: `${formatNumber(limits.tenants)} ${limits.tenants === 1 ? l.singular : l.plural}`,
    });
  }

  const monthly: HighlightItem[] = [];
  const regIncluded = limits.registrations_included;
  const regCap = limits.registrations_hard_cap;
  if (typeof regIncluded === 'number') {
    const detail = typeof regCap === 'number' && regCap > regIncluded
      ? `up to ${formatNumber(regCap)} with top-ups`
      : undefined;
    monthly.push({
      key: 'registrations',
      label: `${formatNumber(regIncluded)} registrations / mo`,
      detail,
    });
  }
  // Gameplays — read canonical `gameplay_tokens`, fall back to the retired
  // `session_tokens` key so cached Stripe plans from the brief period when we
  // granted against that key still render sensibly.
  const gameplays = grantMap.get('gameplay_tokens') || grantMap.get('session_tokens') || 0;
  if (gameplays > 0) {
    monthly.push({ key: 'gameplays', label: `${formatNumber(gameplays)} gameplays / mo` });
  }
  const userSessions = grantMap.get('user_session_tokens') || 0;
  if (userSessions > 0) {
    monthly.push({ key: 'user_sessions', label: `${formatNumber(userSessions)} user sessions / mo` });
  }
  const prizeAllocations = grantMap.get('prize_allocation_tokens') || grantMap.get('voucher_tokens') || 0;
  if (prizeAllocations > 0) {
    monthly.push({ key: 'prize_allocations', label: `${formatNumber(prizeAllocations)} prize allocations / mo` });
  }

  const aiMessaging: HighlightItem[] = [];
  const generation = grantMap.get('generation_tokens') || 0;
  if (generation > 0) {
    aiMessaging.push({
      key: 'generation',
      label: `${formatNumber(generation)} generation tokens / mo`,
      detail: 'For AI-generated quiz content',
    });
  }
  const whatsapp = grantMap.get('whatsapp_credits') || 0;
  if (whatsapp > 0) {
    aiMessaging.push({
      key: 'whatsapp',
      label: `${formatNumber(whatsapp)} WhatsApp messages / mo`,
      detail: 'Outbound campaign broadcasts',
    });
  }

  const groups: HighlightGroup[] = [];
  if (capacity.length) groups.push({ title: 'Capacity', items: capacity });
  if (monthly.length) groups.push({ title: 'Monthly allowances', items: monthly });
  if (aiMessaging.length) groups.push({ title: 'AI & messaging', items: aiMessaging });
  return groups;
}

async function buy(priceId: string) {
  isLoading.value = true;
  try {
    const stripeUrl = await billingService.startCheckout(priceId);
    window.location.href = stripeUrl;
  } catch (err) {
    alert("Something went wrong starting checkout.");
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="billing-page">
    <p class="billing-subtitle">Manage your plan, top up tokens, and check your account limits. Upgrade or downgrade any time.</p>

    <!-- Loading skeleton -->
    <div v-if="isLoading" class="billing-loading">
      <div class="skeleton-dashboard">
        <div class="skeleton-block skeleton-pulse" style="height: 120px;"></div>
      </div>
      <div class="skeleton-grid">
        <div v-for="n in 3" :key="n" class="skeleton-card skeleton-pulse"></div>
      </div>
    </div>

    <template v-else>
      <!-- Dashboard (always visible — global usage context above the tabs) -->
      <section class="billing-dashboard">
        <div class="dash-panel">
          <h3 class="dash-label">Your Token balance</h3>
          <p v-if="orgError" class="dash-error">{{ orgError }}</p>
          <div class="dash-items">
            <div v-if="Object.keys(wallets).length === 0" class="dash-item dash-item--empty">
              <span class="dash-item-value">0</span>
              <span class="dash-item-label">Tokens</span>
            </div>
            <template v-else v-for="(sources, type) in displayWallets" :key="type">
              <div
                class="dash-item"
                :title="`${Object.entries(sources).map(([s, a]) => `${formatName(s)}: ${a}`).join(', ')}`"
              >
                <MetricIcon class="dash-item-icon" :metric-key="(type as string)" />
                <span class="dash-item-value">{{ getTokenTotal(sources) }}</span>
                <span class="dash-item-label">{{ formatName(type as string) }}</span>
              </div>
            </template>
          </div>
        </div>

        <div class="dash-divider"></div>

        <div class="dash-panel">
          <h3 class="dash-label">Your Account limits</h3>
          <p v-if="orgError" class="dash-error">{{ orgError }}</p>
          <div class="dash-items">
            <div v-if="Object.keys(limits).length === 0" class="dash-item dash-item--empty">
              <span class="dash-item-value">--</span>
              <span class="dash-item-label">No limits set</span>
            </div>
            <div v-else v-for="(limit, type) in limits" :key="type" class="dash-item">
              <MetricIcon class="dash-item-icon" :metric-key="(type as string)" />
              <span class="dash-item-value">{{ formatUsageCell(type as string, limit) }}</span>
              <span class="dash-item-label">
                {{ formatName(type as string) }}
                <span v-if="getUsageForLimit(type as string)?.hint" class="dash-item-hint">
                  ({{ getUsageForLimit(type as string)?.hint }})
                </span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <SectionTabs v-model="activeTab" :tabs="billingTabs" aria-label="Billing tabs" />

      <!-- Subscription Plans -->
      <section v-if="activeTab === 'plan' && subscriptions.length > 0" class="billing-section">
        <h2 class="section-heading">Monthly plans</h2>
        <div class="plans-grid">
          <div
            v-for="plan in subscriptions"
            :key="plan.id"
            class="plan-card"
            :class="{ 'plan-card--featured': isProfessional(plan) }"
          >
            <div v-if="isProfessional(plan)" class="plan-badge">Recommended</div>
            <h3 class="plan-name">{{ plan.name }}</h3>
            <div class="plan-price">
              <span class="plan-price-amount">{{ formatPrice(plan.amount, plan.currency) }}</span>
              <span class="plan-price-period">/mo</span>
            </div>

            <!-- "What you get" — concrete limits + monthly token allowances, grouped -->
            <div class="plan-highlights-body">
              <div
                v-for="group in buildPlanHighlights(plan)"
                :key="group.title"
                class="plan-highlight-group"
              >
                <h4 class="plan-highlight-heading">{{ group.title }}</h4>
                <ul class="plan-highlights">
                  <li v-for="item in group.items" :key="item.key">
                    <span class="plan-highlight-label">{{ item.label }}</span>
                    <span v-if="item.detail" class="plan-highlight-detail">{{ item.detail }}</span>
                  </li>
                </ul>
              </div>
              <p v-if="buildPlanHighlights(plan).length === 0" class="plan-highlights-empty">
                Plan contents syncing…
              </p>

              <!-- Feature capabilities (collapsed / secondary) -->
              <details v-if="plan.features && plan.features.length > 0" class="plan-features-details">
                <summary>Capabilities included</summary>
                <ul class="plan-features">
                  <li v-for="feature in plan.features" :key="feature">
                    {{ formatFeature(feature) }}
                  </li>
                </ul>
              </details>
            </div>

            <div class="plan-actions">
              <button
                class="plan-btn"
                :class="{ 'plan-btn--primary': isProfessional(plan) || plan.amount > 0, 'plan-btn--outline': plan.amount === 0 }"
                @click="buy(plan.id)"
              >
                {{ plan.amount === 0 ? 'Start Free' : 'Subscribe' }}
              </button>
            </div>
          </div>
        </div>
      </section>
      <p v-else-if="activeTab === 'plan' && plansError" class="billing-error">{{ plansError }}</p>

      <!-- Token Top-Ups -->
      <section v-if="activeTab === 'tokens' && tokenTopUps.length > 0" class="billing-section">
        <h2 class="section-heading">Token top-ups</h2>
        <p class="section-description">Need more plays? Grab a token pack.</p>
        <div class="topup-grid">
          <button
            v-for="plan in tokenTopUps"
            :key="plan.id"
            class="topup-card"
            @click="buy(plan.id)"
          >
            <span class="topup-name">{{ plan.name }}</span>
            <span class="topup-price">{{ formatPrice(plan.amount, plan.currency) }}</span>
          </button>
        </div>
      </section>

      <!-- Expansions (recurring capacity add-ons) -->
      <section v-if="activeTab === 'expansions' && expansions.length > 0" class="billing-section">
        <h2 class="section-heading">Expansions</h2>
        <p class="section-description">Need more capacity? Recurring monthly add-ons that raise your account limits.</p>
        <div class="topup-grid">
          <button
            v-for="plan in expansions"
            :key="plan.id"
            class="topup-card topup-card--limit"
            @click="buy(plan.id)"
          >
            <span class="topup-name">{{ plan.name }}</span>
            <span class="topup-price">
              {{ formatPrice(plan.amount, plan.currency) }}<small class="topup-period">/mo</small>
            </span>
          </button>
        </div>
      </section>

    </template>
  </div>
</template>

<style scoped>
.billing-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0;
  font-family: var(--font-sans);
  color: var(--vt-c-text-primary);
}

.billing-subtitle {
  color: var(--vt-c-text-muted);
  font-size: 0.95rem;
  font-weight: 300;
  margin: 0 0 1.25rem;
  opacity: 0.75;
}

.billing-section--placeholder {
  background-color: rgba(44, 62, 80, 0.5);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  padding: 1.5rem;
}

.placeholder-list {
  margin: 0.75rem 0 0;
  padding-left: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.placeholder-list li {
  font-size: 0.88rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);
}
.placeholder-list li::marker {
  color: rgba(150, 255, 201, 0.5);
}

/* ── Loading skeleton ── */
.billing-loading {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.skeleton-dashboard {
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}

.skeleton-block {
  border-radius: var(--base-layout-card-border-radius);
  background: rgba(var(--vt-c-accent-rgb), 0.06);
  border: 1px solid var(--vt-c-border);
}

.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.25rem;
}

.skeleton-card {
  height: 320px;
  border-radius: var(--base-layout-card-border-radius);
  background: rgba(var(--vt-c-accent-rgb), 0.06);
  border: 1px solid var(--vt-c-border);
}

.skeleton-pulse {
  animation: pulse 1.8s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* ── Dashboard ── */
.billing-dashboard {
  display: flex;
  align-items: flex-start;
  gap: clamp(1.5rem, 4vw, 3rem);
  background: var(--ui-inverse-surface);
  border: 1px solid var(--vt-c-border);
  border-radius: var(--base-layout-card-border-radius);
  padding: clamp(1.25rem, 3vw, 2rem) clamp(1.5rem, 3vw, 2.5rem);
  margin-bottom: clamp(2.5rem, 5vw, 4rem);
  max-width: 700px;
  margin-inline: auto;
}

.dash-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.dash-label {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #ffffff;
  margin: 0;
}

.dash-error {
  color: #f6ad55;
  font-size: 0.85rem;
  margin: 0;
  text-align: center;
}

.dash-items {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
  width: 100%;
}

.dash-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: rgba(var(--vt-c-accent-rgb), 0.08);
  border: 1px solid var(--vt-c-border);
  min-width: 0;
  cursor: default;
}

.dash-item-icon {
  color: #96ffc9;
  margin-bottom: 0.15rem;
  opacity: 0.85;
}

.dash-item--empty {
  background: transparent;
  border-style: dashed;
}

.dash-item-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--vt-c-white);
  line-height: 1;
}

.dash-item--empty .dash-item-value {
  color: var(--vt-c-text-muted);
}

.dash-item-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--vt-c-text-muted);
  margin-top: 0.35rem;
}

.dash-item-hint {
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  opacity: 0.75;
  margin-left: 0.25rem;
}

.dash-divider {
  width: 1px;
  align-self: stretch;
  background: var(--vt-c-divider-1);
  flex-shrink: 0;
}

/* ── Section headings ── */
.billing-section {
  margin-bottom: clamp(2.5rem, 5vw, 4rem);
}

.section-heading {
  text-align: center;
  font-size: clamp(1.3rem, 3vw, 1.8rem);
  font-weight: 800;
  color: var(--vt-c-white);
  margin: 0 0 0.5rem;
}

.section-description {
  text-align: center;
  color: var(--vt-c-text-muted);
  font-size: 0.95rem;
  margin: 0 0 1.5rem;
}

.billing-error {
  text-align: center;
  color: #f6ad55;
  font-size: 0.95rem;
  margin-bottom: 2rem;
}

/* ── Plan cards ── */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  align-items: stretch;
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--ui-inverse-surface);
  border: 1px solid var(--vt-c-border);
  border-radius: var(--base-layout-card-border-radius);
  padding: clamp(1.25rem, 3vw, 2rem);
  transition: transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease;
}

.plan-card:hover {
  transform: translateY(-4px);
  border-color: var(--vt-c-border-hover);
  box-shadow: 0 8px 24px rgba(var(--vt-c-accent-rgb), 0.08);
}

.plan-card--featured {
  border-color: var(--vt-c-accent);
  background: var(--ui-inverse-surface);
  box-shadow: 0 0 0 1px var(--vt-c-accent), 0 8px 24px rgba(var(--vt-c-accent-rgb), 0.12);
}

.plan-badge {
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 14px;
  border-radius: 999px;
  white-space: nowrap;
}

.plan-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--vt-c-white);
  margin: 0 0 0.75rem;
  text-align: center;
}

.plan-price {
  text-align: center;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--vt-c-divider-1);
}

.plan-price-amount {
  font-size: 2rem;
  font-weight: 800;
  color: var(--vt-c-white);
}

.plan-price-period {
  font-size: 0.9rem;
  color: var(--vt-c-text-muted);
  font-weight: 500;
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  flex-grow: 1;
}

.plan-features li {
  position: relative;
  padding-left: 1.4rem;
  margin-bottom: 0.65rem;
  color: var(--vt-c-text-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
}

.plan-features li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.45em;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--vt-c-accent);
}

/* ── Plan highlights: the concrete "what you get" list (grouped) ── */
.plan-highlights-body {
  flex: 1 1 auto;       /* Take up available space so plan-actions is pushed to bottom */
  min-height: 0;
  margin-bottom: 1.5rem;
}

.plan-highlight-group {
  margin-bottom: 1.1rem;
}

.plan-highlight-group:last-child {
  margin-bottom: 0;
}

.plan-highlight-heading {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vt-c-accent);
  margin: 0 0 0.6rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid rgba(var(--vt-c-accent-rgb), 0.18);
}

.plan-highlights {
  list-style: none;
  padding: 0;
  margin: 0;
}

.plan-highlights li {
  position: relative;
  padding-left: 1.6rem;
  margin-bottom: 0.7rem;
  color: var(--vt-c-white);
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
}

.plan-highlights li:last-child {
  margin-bottom: 0;
}

.plan-highlights li::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0;
  color: var(--vt-c-accent);
  font-weight: 800;
  font-size: 0.95rem;
}

.plan-highlight-label {
  display: block;
}

.plan-highlight-detail {
  display: block;
  margin-top: 0.2rem;
  color: var(--vt-c-text-muted);
  font-size: 0.8rem;
  font-weight: 400;
  font-style: italic;
}

.plan-highlights-empty {
  color: var(--vt-c-text-muted);
  font-style: italic;
  font-size: 0.9rem;
  margin: 0;
}

.plan-features-details {
  margin-top: 1rem;
  font-size: 0.85rem;
}

.plan-features-details summary {
  cursor: pointer;
  color: var(--vt-c-text-muted);
  margin-bottom: 0.5rem;
  outline: none;
  list-style: none;
}

.plan-features-details summary::-webkit-details-marker {
  display: none;
}

.plan-features-details summary::before {
  content: '▸ ';
  display: inline-block;
  margin-right: 0.25rem;
  transition: transform 150ms ease;
}

.plan-features-details[open] summary::before {
  content: '▾ ';
}

.plan-features-details .plan-features {
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
}

.plan-features-details .plan-features li {
  font-size: 0.8rem;
  margin-bottom: 0.4rem;
}

/* ── Plan actions (pinned to card bottom) ── */
.plan-actions {
  margin-top: auto;       /* Pushes this block to the bottom of .plan-card (flex col) */
  padding-top: 0.5rem;    /* Visual breathing room from highlight body above */
  display: flex;
  flex-direction: column;
}

/* ── Buttons ── */
.plan-btn {
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 180ms ease;
  border: none;
  font-family: var(--font-sans);
}

.plan-btn:active,
.plan-btn:focus-visible {
  background: #50e5ff;
  color: var(--color-background);
  border-color: #50e5ff;
}

.plan-btn--primary {
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.plan-btn--primary:hover {
  background: var(--vt-c-accent-light);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(var(--vt-c-accent-rgb), 0.3);
}

.plan-btn--outline {
  background: var(--vt-c-accent);
  color: var(--color-background);
  border: 1px solid var(--vt-c-accent);
}

.plan-btn--outline:hover {
  background: var(--vt-c-accent-light);
  transform: translateY(-2px);
}

/* ── Top-up / expansion cards ── */
.topup-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.85rem;
  margin-top: 1rem;
}

.topup-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  min-height: 3.5rem;
  text-align: left;
  border-radius: var(--base-layout-card-border-radius);
  background: rgba(var(--vt-c-accent-rgb), 0.06);
  border: 1px solid var(--vt-c-border);
  cursor: pointer;
  transition: all 180ms ease;
  font-family: var(--font-sans);
  color: var(--vt-c-white);
}

.topup-card:hover {
  border-color: var(--vt-c-border-hover);
  background: rgba(var(--vt-c-accent-rgb), 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--vt-c-accent-rgb), 0.08);
}

.topup-card--limit {
  border-color: rgba(99, 102, 241, 0.25);
  background: rgba(99, 102, 241, 0.06);
}

.topup-card--limit:hover {
  border-color: rgba(99, 102, 241, 0.4);
  background: rgba(99, 102, 241, 0.1);
}

.topup-name {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.25;
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
}

.topup-price {
  font-size: 1rem;
  font-weight: 800;
  color: var(--vt-c-accent);
  flex: 0 0 auto;
  white-space: nowrap;
}

.topup-period {
  font-size: 0.7rem;
  font-weight: 600;
  opacity: 0.7;
  margin-left: 0.15rem;
}

.topup-card--limit .topup-price {
  color: #a78bfa;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .billing-dashboard {
    flex-direction: column;
    align-items: stretch;
  }

  .dash-divider {
    width: 100%;
    height: 1px;
    align-self: auto;
  }

  .dash-items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dash-item {
    min-width: 0;
    padding: 0.65rem 1rem;
  }

  /* Plan cards stack full-width on phones, tighter internal padding but
     keep the grouped heading + sub-detail structure intact. */
  .plans-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .plan-card {
    padding: 1.25rem;
  }

  .plan-highlights li {
    font-size: 0.92rem;
  }

  .plan-highlight-heading {
    font-size: 0.68rem;
  }
}
</style>
